import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as client from "../../../client";
import {
  createPolicyApplications,
  getPolicyApplicationFields,
  getPolicyApplicationForms,
  listPolicyApplications,
  updateApplicationForm,
  updatePolicyApplication,
  updatePolicyOverlay,
} from "../../../api/routes";
import { policyApplicationKeys } from "../queryKeys";
import { pairPartner } from "./formEntries";

export const usePolicyApplications = (reportId: string) =>
  useQuery({
    queryKey: policyApplicationKeys.forReport(reportId),
    queryFn: () => listPolicyApplications(reportId),
    enabled: reportId !== "",
  });

export const useCreatePolicyApplications = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => createPolicyApplications(reportId),
    onSuccess: (applications) => {
      queryClient.setQueryData(
        policyApplicationKeys.forReport(reportId),
        applications
      );
    },
  });
};

export const usePolicyApplicationFields = (applicationId: string) =>
  useQuery({
    queryKey: policyApplicationKeys.fields(applicationId),
    queryFn: () => getPolicyApplicationFields(applicationId),
  });

export const useUpdatePolicyOverlay = (applicationId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      overlay: client.PolicyFieldOverlay;
      notes: string | null;
    }) => updatePolicyOverlay(applicationId, vars.overlay, vars.notes),
    onSuccess: (result) => {
      queryClient.setQueryData(
        policyApplicationKeys.fields(applicationId),
        result
      );
      // The report roster shows status; keep it fresh after an edit.
      queryClient.invalidateQueries({
        queryKey: policyApplicationKeys.forReport(result.application.report_id),
      });
    },
  });
};

export const useUpdatePolicyApplication = (applicationId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { status: client.PolicyApplicationStatus }) =>
      updatePolicyApplication(applicationId, vars.status),
    onSuccess: (application) => {
      queryClient.setQueryData<client.PolicyApplicationFieldsResponse>(
        policyApplicationKeys.fields(applicationId),
        (previous) => (previous ? { ...previous, application } : previous)
      );
      queryClient.invalidateQueries({
        queryKey: policyApplicationKeys.forReport(application.report_id),
      });
    },
  });
};

export const usePolicyApplicationForms = (applicationId: string) =>
  useQuery({
    queryKey: policyApplicationKeys.forms(applicationId),
    queryFn: () => getPolicyApplicationForms(applicationId),
  });

export const useUpdateApplicationForm = (applicationId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { formId: string; enabled: boolean }) =>
      updateApplicationForm(applicationId, vars.formId, vars.enabled),
    // Flip the switch optimistically so rapid toggling feels instant; the
    // authoritative response (or an error rollback) reconciles after.
    onMutate: async (vars) => {
      const key = policyApplicationKeys.forms(applicationId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous =
        queryClient.getQueryData<client.PolicyApplicationFormsResponse>(key);
      if (previous) {
        // Enabling one member of a pair turns the other off, as the server does.
        const partner = vars.enabled
          ? pairPartner(previous.pairs, vars.formId)
          : null;
        queryClient.setQueryData<client.PolicyApplicationFormsResponse>(key, {
          ...previous,
          forms: previous.forms.map((form) => {
            if (form.locked) return form;
            if (form.form_id === vars.formId)
              return { ...form, enabled: vars.enabled };
            if (form.form_id === partner) return { ...form, enabled: false };
            return form;
          }),
        });
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          policyApplicationKeys.forms(applicationId),
          context.previous
        );
      }
    },
    onSuccess: (result) => {
      queryClient.setQueryData(
        policyApplicationKeys.forms(applicationId),
        result
      );
    },
  });
};
